import styles from './PartnerCostsVehicleCostContent.module.scss';
import React, {useCallback, useMemo, useState} from 'react';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {
  Icon,
  IconDelete,
  IconDeleteSolid,
  IconEditSolid,
  IconOfferSolid,
  IconCopy
} from '@flixbus/honeycomb-icons-react';
import {
  Box,
  Button,
  Flag,
  Grid,
  GridCol,
  Heading,
  Link,
  spaceHelpers
} from '@flixbus/honeycomb-react';
import {confirm} from '@/Pages/Common/Confirm';
import {useToast} from '@/Pages/Common/toasts-container';
import {useLocalUnitsContext} from '@/Pages/Common/LocalUnits';
import {deleteVehicleTypeCost} from '@/Pages/PartnerCosts/store/thunks';
import {Nullable} from '@/Pages/Common/types';
import {Table, TableAction} from '@/components/Table';
import {PartnerCostsBusTypeCost} from '@/Pages/PartnerCosts/store/types';
import {
  RegionalCostsBusTypeCost,
  RegionalCostsVehicleCost
} from '@/Pages/RegionalCosts/store/types';
import {initVehicleTypeCosts} from '@/components/VehicleTypeCosts/VehicleTypeCosts.data';
import {VehicleTypeCostsPanelState} from '@/components/VehicleTypeCosts/VehicleTypeCostsPanel/VehicleTypeCostsPanel.types';
import {getBaseCostsPerVehicleTypeTableConfig} from '@/components/VehicleTypeCosts/VehicleTypeCosts.utils';
import PartnerCostsVehicleTypeCostsPanel from '@/Pages/PartnerCosts/components/PartnerCostsVehicleCostTab/PartnerCostsVehicleTypeCostsPanel/PartnerCostsVehicleTypeCostsPanel';
import {PartnerCostsVehicleCostsContentProps} from './PartnerCostsVehicleCostContent.types';
import {
  getCostPerVehicleTypeHeaders,
  getPartnerCostsVehicleCostTableConfig
} from './PartnerCostsVehicleCostContent.data';
import {RegionalCostsPath} from '@/Pages/RegionalCosts/constants/routePaths';
import VehicleCostTable from '@/components/VehicleCosts/VehicleCostsTable/VehicleCostsTable';
import {useUpdateBusPartnerBusCostEntryV2Mutation} from '@/Pages/PartnerCosts/hooks/useUpdateBusPartnerBusCostEntryV2Mutation';
import {getRegionalCostsVehicleCostTableConfig} from '@/Pages/RegionalCosts/components/RegionalCostsVehicleCostTab/RegionalCostsVehicleCostContent/RegionalCostsVehicleCostContent.data';

const PartnerCostsVehicleCostsContent: React.FC<
  PartnerCostsVehicleCostsContentProps
> = ({
  busPartner,
  year,
  partnerVehicleCost,
  regionalVehicleCost,
  vehicleTypes,
  partnerVehicleTypeCosts,
  regionalVehicleTypeCosts,
  currency,
  partnerCountry
}) => {
  const {3: space3, 5: space5} = spaceHelpers();
  const dispatch = useAppDispatch();

  const {localUnits} = useLocalUnitsContext();
  const {addToast} = useToast();

  const {updateBusPartnerBusCostEntry} =
    useUpdateBusPartnerBusCostEntryV2Mutation();
  const updateVehicleCosts = useCallback(
    async (updatedCosts: RegionalCostsVehicleCost) => {
      await updateBusPartnerBusCostEntry(updatedCosts, busPartner.id, year);
    },
    [busPartner.id, updateBusPartnerBusCostEntry, year]
  );

  const [panelState, setPanelState] = useState<VehicleTypeCostsPanelState>({
    data: null,
    loading: false,
    error: null
  });

  const vehicleCostsConfig = useMemo(
    () =>
      getPartnerCostsVehicleCostTableConfig(
        partnerVehicleCost,
        partnerCountry,
        localUnits
      ),
    [localUnits, partnerCountry, partnerVehicleCost]
  );
  const fallbackVehicleCostsConfig = useMemo(
    () =>
      regionalVehicleCost &&
      getRegionalCostsVehicleCostTableConfig(
        regionalVehicleCost,
        partnerCountry,
        localUnits
      ),
    [localUnits, partnerCountry, regionalVehicleCost]
  );

  const getCostsPerVehicleTypeTableConfig = useCallback(
    (
      vehicleTypeCosts:
        | PartnerCostsBusTypeCost[]
        | RegionalCostsBusTypeCost[]
        | undefined,
      actions: TableAction[]
    ) => {
      const {headers, rows} = getBaseCostsPerVehicleTypeTableConfig(
        vehicleTypes,
        vehicleTypeCosts,
        partnerCountry,
        getCostPerVehicleTypeHeaders(currency, localUnits)
      );

      return {headers, rows, actions};
    },
    [partnerCountry, vehicleTypes, currency, localUnits]
  );

  const regionalCostsPerVehicleTypeActions: TableAction[] = [
    {
      icon: IconCopy,
      onClick: row => {
        setPanelState({
          data: {...row, id: null} as Nullable<PartnerCostsBusTypeCost>,
          loading: false,
          error: null,
          saveButtonName: 'Duplicate'
        });
      }
    }
  ];

  const regionalCostsPerVehicleType = getCostsPerVehicleTypeTableConfig(
    regionalVehicleTypeCosts,
    regionalCostsPerVehicleTypeActions
  );

  const partnerCostsPerVehicleTypeActions: TableAction[] = [
    {
      icon: IconEditSolid,
      onClick: row => {
        setPanelState({
          data: row as Nullable<PartnerCostsBusTypeCost>,
          loading: false,
          error: null
        });
      }
    },
    {
      icon: IconDeleteSolid,
      appearance: 'danger',
      onClick: row => {
        confirm({
          PopupIcon: IconDelete,
          title: 'Delete this vehicle type cost?',
          message: ``
        }).then(confirmed => {
          if (confirmed) {
            dispatch(deleteVehicleTypeCost(row.id, addToast));
          }
        });
      }
    }
  ];

  const partnerCostsPerVehicleType = getCostsPerVehicleTypeTableConfig(
    partnerVehicleTypeCosts,
    partnerCostsPerVehicleTypeActions
  );

  const flagConfig = useMemo(() => {
    const {name, countryCodes} = partnerCountry;

    if (countryCodes.length === 0) return null;

    const lowerCaseCountryCode = countryCodes[0].toLowerCase();
    return {
      countryCode: lowerCaseCountryCode,
      countryName: name
    };
  }, [partnerCountry]);

  return (
    <>
      <div className={styles.header}>
        <Heading size={3}>
          Cost for {busPartner.name} ({year})
        </Heading>
        <div className={styles.subtitle}>
          On this page, you can define the country cost factors for specific
          partners. Partner-level values will override any existing regional
          values. If available, regional values are shown in brackets for
          reference.{' '}
        </div>
        <div className={styles.subtitle}>
          Fields marked with an * are mandatory and values must be specified at
          either regional or partner level.
        </div>
      </div>

      <section data-testid="base-costs">
        <Box>
          <Grid>
            <GridCol size={6}>
              <VehicleCostTable
                country={partnerCountry}
                year={year}
                config={vehicleCostsConfig}
                vehicleCost={partnerVehicleCost}
                fallbackConfig={fallbackVehicleCostsConfig}
                fallbackVehicleCost={regionalVehicleCost}
                updateFn={updateVehicleCosts}
              />
            </GridCol>
          </Grid>
        </Box>
      </section>

      <section data-testid="partner-costs-per-vehicle-type">
        <Box extraClasses={space5.top}>
          <Grid extraClasses={space3.bottom}>
            <GridCol>
              <Heading size={3}>
                {busPartner.name}-specific costs per vehicle type
              </Heading>
            </GridCol>
            <GridCol inline>
              <Button
                onClick={() => {
                  setPanelState({
                    data: initVehicleTypeCosts,
                    loading: false,
                    error: null
                  });
                }}
              >
                <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
                Add vehicle type
              </Button>
            </GridCol>
          </Grid>

          <Table
            headers={partnerCostsPerVehicleType.headers}
            rows={partnerCostsPerVehicleType.rows}
            actions={partnerCostsPerVehicleType.actions}
            caption={
              partnerCostsPerVehicleType.rows.length === 0
                ? 'No entries specified on partner level.'
                : undefined
            }
          />
        </Box>

        {/* Is used for both create and update logic */}
        <PartnerCostsVehicleTypeCostsPanel
          partnerId={busPartner.id}
          year={year}
          currency={currency}
          vehicleTypes={vehicleTypes}
          localUnits={localUnits}
          panelState={panelState}
          setPanelState={setPanelState}
          partnerCountry={partnerCountry}
        />
      </section>

      <section data-testid="regional-costs-per-vehicle-type">
        <Box extraClasses={space5.top}>
          <Grid>
            {flagConfig ? (
              <GridCol inline extraClasses="flex-center">
                <Flag
                  countryCode={flagConfig.countryCode}
                  countryName={flagConfig.countryName}
                />
              </GridCol>
            ) : null}
            <GridCol>
              <Heading size={3}>Country default costs per vehicle type</Heading>
            </GridCol>
          </Grid>

          <div className={styles.regionalSpecificCostTitle}>
            Here are the country-level cost that will be applied when
            partner-specific data is missing or incomplete. You can edit these
            defaults under{' '}
            <Link
              href={`/${RegionalCostsPath.vehicleCosts}?countryId=${partnerCountry.id}&year=${year}`}
              target="_blank"
            >
              Regional Costs
            </Link>
            .
          </div>

          <Table
            headers={regionalCostsPerVehicleType.headers}
            rows={regionalCostsPerVehicleType.rows}
            actions={regionalCostsPerVehicleType.actions}
            caption={
              regionalCostsPerVehicleType.rows.length === 0
                ? 'No entries specified on country level.'
                : undefined
            }
          />
        </Box>
      </section>
    </>
  );
};

export default React.memo(PartnerCostsVehicleCostsContent);
